import { Heading, Container, Tag } from '@boltenergy-be/design-system'
import Link from 'components/link/Link'
import styles from 'components/footer/Footer.module.scss'
import { useTranslation } from 'next-i18next/pages'
import SubscribeForm from './subscribe-form/SubscribeForm'
import { SOCIALS } from 'components/footer/Footer.constants'
import { routes } from 'utils/routes'
import { Language } from 'types/language'
import { useGetLanguage } from 'hooks/useGetLanguage'
import { useGetJobOffersQuery } from 'store/api/cms-api'
import Logo from 'assets/Logo'
import { TAKEOVER_DOCUMENT_URL } from 'constants/constants'

/**
 * The site footer: newsletter signup, page links, socials and legal links.
 *
 * @figma Footer (Web Sections) https://www.figma.com/design/bb4ffCw3RlErklnLuAM4DN/Web-Sections?node-id=7-10471 — Breakpoint: n/a, Brand: n/a (Bolt only), Language: n/a (from the locale)
 */
const Footer = () => {
  // i18n
  const { t } = useTranslation(['footer', 'common'])
  const language = useGetLanguage()

  // Redux
  const { data: jobOffers } = useGetJobOffersQuery(language)

  // Constants
  const numberOfJobOffersForLocale = jobOffers?.filter((jobOffer) => jobOffer.locale === language).length ?? 0

  return (
    <Container as="footer" color="dark" classnames={{ content: styles.content, container: styles.container }}>
      <section className={styles.subscribe}>
        <Heading as="h2" className="mb-500" serif>
          {t(($) => $.subscribe.title, { ns: 'common' })}
        </Heading>

        <SubscribeForm />
      </section>

      <nav className={styles.pages}>
        <ul>
          <li>
            <Link size="small" href={routes(language).priceList}>
              {t(($) => $.priceLists)}
            </Link>
          </li>
          <li>
            <Link className="mr-200" size="small" href={routes(language).careers}>
              {t(($) => $.careers)}
            </Link>
            {numberOfJobOffersForLocale > 0 && (
              <Tag type="positive" size="small" className={styles['job-offer-tag']}>
                {numberOfJobOffersForLocale}
              </Tag>
            )}
          </li>
          <li>
            <Link
              size="small"
              target="_blank"
              href={language === Language.DUTCH ? 'https://bolt.prezly.com' : 'https://bolt.prezly.com/fr'}
              rel="noopener noreferrer"
            >
              {t(($) => $.press)}
            </Link>
          </li>
          <li>
            <Link size="small" href={TAKEOVER_DOCUMENT_URL[language]} download={true} target="_blank" rel="noopener noreferrer">
              {t(($) => $.energyTransitionDocument)}
            </Link>
          </li>
          <li>
            <Link size="small" href={routes(language).uspsOverview}>
              {t(($) => $.usps)}
            </Link>
          </li>
          <li>
            <Link size="small" href={routes(language).complaint}>
              {t(($) => $.complaint)}
            </Link>
          </li>
        </ul>
      </nav>

      <ul className={styles.socials}>
        {Array.from(SOCIALS.entries()).map(([key, social]) => (
          <li key={key}>
            <Link size="small" as="a" target="_blank" leadingIcon={social.icon} href={social.url}>
              {social.title}
            </Link>
          </li>
        ))}
      </ul>

      <div className={styles.bolt}>
        <p>
          &copy; {new Date().getFullYear()} Bolt Energie BV / {t(($) => $.address)} / BE 0726.633.730
        </p>

        <ul className={styles['terms-and-conditions']}>
          <li>
            <Link size="small" variant="inline" href={routes(language).terms}>
              {t(($) => $.termsAndConditions)}
            </Link>
          </li>
          <li>
            <Link size="small" variant="inline" href={routes(language).privacy}>
              {t(($) => $.privacyPolicy)}
            </Link>
          </li>
        </ul>
      </div>

      <Logo className={styles.logo} />
    </Container>
  )
}

export default Footer
